import { createApi } from "@reduxjs/toolkit/query/react"
import { setSelectedSequence } from "../campaignSetupSlice"
import { baseQueryWithReauth } from "@/core/api/baseQuery"
import { buildQueryString } from "@/utils/strings"
import { addAndEditArrayById } from "@/utils/arrays"
import type { RootState } from "../store"
import type {
  Sequence,
  SequenceAPIResponse,
  SequencePreviewPayload,
  SequenceSpamTestListResponse,
  SequenceSpamTestOptionsResponse,
  SequenceSpamTestPayload,
  SequenceSpamTestPromptOverridePayload,
  SequenceSpamTestPromptOverrideResponse,
  SequenceSpamTestRun,
  SequenceSpamTestRunTestPayload,
  SequenceVariable,
} from "@/types/entities/sequence"
import type { PaginationProps } from "./types"

type SequenceSpamTestRunData =
  | SequenceSpamTestRun
  | { run: SequenceSpamTestRun }

type SequenceSpamTestListData =
  | SequenceSpamTestListResponse
  | SequenceSpamTestRun[]

const getSequenceSpamTestRunId = (run?: SequenceSpamTestRun) =>
  run?._id || run?.id || run?.runId

const transformSequenceSpamTestRun = ({
  data,
}: {
  data: SequenceSpamTestRunData
}) => ("run" in data ? data.run : data)

const transformSequenceSpamTestList = ({
  data,
}: {
  data: SequenceSpamTestListData
}) => (Array.isArray(data) ? { items: data, total: data.length } : data)

export const sequencesApi = createApi({
  reducerPath: "sequencesApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["Sequences", "Sequence Variables", "Sequence Spam Tests"],
  endpoints: (builder) => ({
    getSequence: builder.query<SequenceAPIResponse, { id: string }>({
      query: ({ id }) => ({
        method: "GET",
        url: `sequence/${id}`,
      }),
      transformResponse: ({ data }: { data: SequenceAPIResponse }) => data,
      providesTags: (result, error, { id }) => [{ type: "Sequences", id }],
    }),
    getSequences: builder.query<
      { items: SequenceAPIResponse[]; total: number },
      { orgId: string } & PaginationProps & { text?: string }
    >({
      query: ({ orgId, ...keys }) => ({
        method: "GET",
        url: `org/${orgId}/sequences?${buildQueryString(keys)}`,
      }),
      transformResponse: ({
        data: { items, total },
      }: {
        data: { items: SequenceAPIResponse[]; total: number }
      }) => ({
        items,
        total,
      }),
      serializeQueryArgs: ({ endpointName }) => endpointName,
      forceRefetch: ({ currentArg, previousArg }) =>
        currentArg?.skip !== previousArg?.skip ||
        currentArg?.orgId !== previousArg?.orgId ||
        currentArg?.text !== previousArg?.text,
      merge: (currentCacheData, responseData, { arg: { skip, text } }) => {
        if (!skip) {
          currentCacheData.items = []
          currentCacheData.total = 0
        }
        currentCacheData.items = addAndEditArrayById({
          currentArray: currentCacheData.items,
          newItems: responseData.items,
        })
        currentCacheData.total = responseData.total
      },
      providesTags: (result) =>
        result
          ? [
              ...result.items.map(({ _id }) => ({
                type: "Sequences" as const,
                id: _id,
              })),
              "Sequences",
            ]
          : ["Sequences"],
    }),
    addSequence: builder.mutation<SequenceAPIResponse, Sequence>({
      query: (body) => ({
        method: "POST",
        url: "sequence",
        body,
      }),
      transformResponse: ({ data }: { data: SequenceAPIResponse }) => data,
      onQueryStarted: async (createdSequence, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled

          dispatch(
            sequencesApi.util.updateQueryData(
              "getSequences",
              {
                orgId: createdSequence.orgId,
              },
              (draft) => {
                Object.assign(draft, {
                  items: draft.items.concat(data),
                  total: draft.total + 1,
                })
              },
            ),
          )
        } catch (e) {
          console.error(e)
        }
      },
    }),
    editSequence: builder.mutation<
      Sequence,
      { body: Partial<Omit<Sequence, "orgId">>; id: string; orgId: string }
    >({
      query: ({ body, id }) => ({
        method: "PUT",
        url: `sequence/${id}`,
        body,
      }),
      transformResponse: ({ data }: { data: Sequence }) => data,
      onQueryStarted: async ({ id, orgId }, { dispatch, queryFulfilled }) => {
        try {
          const { data: updatedSequence } = await queryFulfilled
          if (updatedSequence) {
            dispatch(
              sequencesApi.util.updateQueryData(
                "getSequences",
                { orgId },
                (draft) => {
                  Object.assign(draft, {
                    items: draft.items.map((sequence) =>
                      sequence._id === id
                        ? {
                            ...updatedSequence,
                          }
                        : sequence,
                    ),
                  })
                },
              ),
            )
          }
        } catch (e) {
          console.error(e)
        }
      },
    }),
    deleteSequence: builder.mutation<
      { success: boolean },
      { id: string; orgId: string }
    >({
      query: ({ id }) => ({
        method: "DELETE",
        url: `sequence/${id}`,
      }),
      onQueryStarted: async ({ id, orgId }, { dispatch, queryFulfilled }) => {
        try {
          const { data: response } = await queryFulfilled
          if (response.success) {
            dispatch(
              sequencesApi.util.updateQueryData(
                "getSequences",
                { orgId },
                (draft) => {
                  let resultTotal = draft.total - 1
                  if (resultTotal) {
                    resultTotal = 0
                  }
                  Object.assign(draft, {
                    items: draft.items.filter(
                      (sequence) => sequence._id !== id,
                    ),
                    total: resultTotal,
                  })
                },
              ),
            )
          }
        } catch (e) {
          console.error(e)
        }
      },
    }),
    toggleSequenceEmail: builder.mutation<
      { data: boolean },
      {
        id: string
        body: { email: number; version?: number; enabled: boolean }
      }
    >({
      query: ({ id, body }) => ({
        method: "PUT",
        url: `sequence/${id}/toggleEmail`,
        body,
      }),
      invalidatesTags: ["Sequences"],
      onQueryStarted: async (
        { body: { enabled, email, version } },
        { dispatch, queryFulfilled, getState },
      ) => {
        const { data } = await queryFulfilled
        if (data.data) {
          const currentSequence = (getState() as RootState).campaignSetup
            .selectedSequence
          if (!currentSequence) {
            return
          }
          dispatch(
            setSelectedSequence({
              ...currentSequence,
              emails: currentSequence.emails.map((v, emailIndex) => {
                return email === emailIndex
                  ? {
                      ...v,
                      enabled: version != null ? v.enabled : enabled,
                      versions:
                        version != null
                          ? v.versions.map((versionValue, versionIndex) =>
                              versionIndex === version
                                ? {
                                    ...versionValue,
                                    enabled,
                                  }
                                : versionValue,
                            )
                          : v.versions,
                    }
                  : {
                      ...v,
                      enabled:
                        !enabled && emailIndex > email ? false : v.enabled,
                    }
              }),
            }),
          )
        }
      },
    }),
    previewSequence: builder.mutation<
      { data: boolean },
      {
        id: string
        body: SequencePreviewPayload
      }
    >({
      query: ({ id, body }) => ({
        method: "POST",
        url: `sequence/${id}/preview`,
        body,
      }),
    }),
    listSequenceSpamTests: builder.query<
      SequenceSpamTestListResponse,
      { id: string }
    >({
      query: ({ id }) => ({
        method: "GET",
        url: `sequence/${id}/spam-tests`,
      }),
      transformResponse: transformSequenceSpamTestList,
      providesTags: (result, error, { id }) =>
        result
          ? [
              ...result.items
                .map(getSequenceSpamTestRunId)
                .filter((runId): runId is string => Boolean(runId))
                .map((runId) => ({
                  type: "Sequence Spam Tests" as const,
                  id: runId,
                })),
              { type: "Sequence Spam Tests", id: `LIST-${id}` },
            ]
          : [{ type: "Sequence Spam Tests", id: `LIST-${id}` }],
    }),
    getSequenceSpamTestOptions: builder.query<
      SequenceSpamTestOptionsResponse,
      { id: string }
    >({
      query: ({ id }) => ({
        method: "GET",
        url: `sequence/${id}/spam-tests/options`,
      }),
      transformResponse: ({
        data,
      }: {
        data: SequenceSpamTestOptionsResponse
      }) => data,
      providesTags: (result, error, { id }) => [
        { type: "Sequence Spam Tests", id: `OPTIONS-${id}` },
      ],
    }),
    getSequenceSpamTestPromptOverride: builder.query<
      SequenceSpamTestPromptOverrideResponse,
      { id: string }
    >({
      query: ({ id }) => ({
        method: "GET",
        url: `sequence/${id}/spam-tests/prompt-override`,
      }),
      transformResponse: ({
        data,
      }: {
        data: SequenceSpamTestPromptOverrideResponse
      }) => data,
      providesTags: (result, error, { id }) => [
        { type: "Sequence Spam Tests", id: `PROMPTS-${id}` },
      ],
    }),
    updateSequenceSpamTestPromptOverride: builder.mutation<
      SequenceSpamTestPromptOverrideResponse,
      { id: string; body: SequenceSpamTestPromptOverridePayload }
    >({
      query: ({ id, body }) => ({
        method: "PUT",
        url: `sequence/${id}/spam-tests/prompt-override`,
        body,
      }),
      transformResponse: ({
        data,
      }: {
        data: SequenceSpamTestPromptOverrideResponse
      }) => data,
      invalidatesTags: (result, error, { id }) => [
        { type: "Sequence Spam Tests", id: `PROMPTS-${id}` },
        { type: "Sequence Spam Tests", id: `OPTIONS-${id}` },
      ],
    }),
    resetSequenceSpamTestPromptOverride: builder.mutation<
      SequenceSpamTestPromptOverrideResponse,
      { id: string }
    >({
      query: ({ id }) => ({
        method: "DELETE",
        url: `sequence/${id}/spam-tests/prompt-override`,
      }),
      transformResponse: ({
        data,
      }: {
        data: SequenceSpamTestPromptOverrideResponse
      }) => data,
      invalidatesTags: (result, error, { id }) => [
        { type: "Sequence Spam Tests", id: `PROMPTS-${id}` },
        { type: "Sequence Spam Tests", id: `OPTIONS-${id}` },
      ],
    }),
    getSequenceSpamTest: builder.query<
      SequenceSpamTestRun,
      { id: string; runId: string }
    >({
      query: ({ id, runId }) => ({
        method: "GET",
        url: `sequence/${id}/spam-tests/${runId}`,
      }),
      transformResponse: transformSequenceSpamTestRun,
      providesTags: (result, error, { runId }) => [
        {
          type: "Sequence Spam Tests",
          id: getSequenceSpamTestRunId(result) || runId,
        },
      ],
    }),
    createSequenceSpamTest: builder.mutation<
      SequenceSpamTestRun,
      { id: string; body: SequenceSpamTestPayload }
    >({
      query: ({ id, body }) => ({
        method: "POST",
        url: `sequence/${id}/spam-tests`,
        body,
      }),
      transformResponse: transformSequenceSpamTestRun,
      invalidatesTags: (result, error, { id }) => {
        const runId = getSequenceSpamTestRunId(result)
        return [
          { type: "Sequence Spam Tests", id: `LIST-${id}` },
          ...(runId
            ? [{ type: "Sequence Spam Tests" as const, id: runId }]
            : []),
        ]
      },
    }),
    generateNextSequenceSpamTestVariation: builder.mutation<
      SequenceSpamTestRun,
      { id: string; runId: string }
    >({
      query: ({ id, runId }) => ({
        method: "POST",
        url: `sequence/${id}/spam-tests/${runId}/variations`,
      }),
      transformResponse: transformSequenceSpamTestRun,
      invalidatesTags: (result, error, { id }) => {
        const runId = getSequenceSpamTestRunId(result)
        return [
          { type: "Sequence Spam Tests", id: `LIST-${id}` },
          ...(runId
            ? [{ type: "Sequence Spam Tests" as const, id: runId }]
            : []),
        ]
      },
    }),
    testSequenceSpamTest: builder.mutation<
      SequenceSpamTestRun,
      { id: string; runId: string; body: SequenceSpamTestRunTestPayload }
    >({
      query: ({ id, runId, body }) => ({
        method: "POST",
        url: `sequence/${id}/spam-tests/${runId}/tests`,
        body,
      }),
      transformResponse: transformSequenceSpamTestRun,
      invalidatesTags: (result, error, { id }) => {
        const runId = getSequenceSpamTestRunId(result)
        return [
          { type: "Sequence Spam Tests", id: `LIST-${id}` },
          ...(runId
            ? [{ type: "Sequence Spam Tests" as const, id: runId }]
            : []),
        ]
      },
    }),
    getSequenceVariables: builder.query<SequenceVariable[], void>({
      query: () => ({
        url: `sequence/variables`,
      }),
      transformResponse: ({ data }: { data: SequenceVariable[] }) => data,
      providesTags: ["Sequence Variables"],
    }),
  }),
})

export const {
  useGetSequenceQuery,
  useGetSequencesQuery,
  useAddSequenceMutation,
  useEditSequenceMutation,
  useDeleteSequenceMutation,
  useToggleSequenceEmailMutation,
  usePreviewSequenceMutation,
  useListSequenceSpamTestsQuery,
  useGetSequenceSpamTestOptionsQuery,
  useGetSequenceSpamTestPromptOverrideQuery,
  useUpdateSequenceSpamTestPromptOverrideMutation,
  useResetSequenceSpamTestPromptOverrideMutation,
  useGetSequenceSpamTestQuery,
  useCreateSequenceSpamTestMutation,
  useGenerateNextSequenceSpamTestVariationMutation,
  useTestSequenceSpamTestMutation,
  useGetSequenceVariablesQuery,
} = sequencesApi
